'use client'

import { useState, useEffect, useCallback } from 'react';
import {
    getProductById,
    getProductBySlug,
    getProductsWithFilters,
    getFeaturedProducts,
    getProductsByCategory,
    searchProducts,
    getProductsByPriceRange,
    getInStockProducts,
    getNewArrivals
} from '@nusrif/services';
import {
    Product,
    ProductFilters,
    ProductSort
} from '@nusrif/types';

interface UseProductsOptions {
    skip?: number;
    take?: number;
    filters?: ProductFilters;
    sort?: ProductSort;
    enabled?: boolean;
}

interface UseProductsReturn {
    products: Product[];
    totalItems: number;
    isLoading: boolean;
    error: string | null;
    refetch: () => Promise<void>;
}

interface UseProductReturn {
    product: Product | null;
    isLoading: boolean;
    error: string | null;
    refetch: () => Promise<void>;
}

/**
 * Hook for fetching a list of products with filtering and pagination
 */
export const useProducts = (options: UseProductsOptions = {}): UseProductsReturn => {
    const { skip = 0, take = 20, filters, sort, enabled = true } = options;
    const [products, setProducts] = useState<Product[]>([]);
    const [totalItems, setTotalItems] = useState(0);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const fetchProducts = useCallback(async () => {
        if (!enabled) return;

        setIsLoading(true);
        setError(null);

        try {
            const response = await getProductsWithFilters({
                skip,
                take,
                filters,
                sort
            });

            if (response.success) {
                setProducts(response.data.items);
                setTotalItems(response.data.totalItems);
            } else {
                setError(response.message || 'Failed to fetch products');
            }
        } catch (err) {
            setError(err instanceof Error ? err.message : 'An error occurred while fetching products');
        } finally {
            setIsLoading(false);
        }
    }, [skip, take, filters, sort, enabled]);

    useEffect(() => {
        fetchProducts();
    }, [fetchProducts]);

    return {
        products,
        totalItems,
        isLoading,
        error,
        refetch: fetchProducts
    };
};

/**
 * Hook for fetching a single product by ID
 */
export const useProduct = (id: string | null): UseProductReturn => {
    const [product, setProduct] = useState<Product | null>(null);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const fetchProduct = useCallback(async () => {
        if (!id) return;

        setIsLoading(true);
        setError(null);

        try {
            const response = await getProductById(id);

            if (response.success) {
                setProduct(response.data);
            } else {
                throw new Error(response.message || 'Failed to fetch product');
            }
        } catch (err) {
            setError(err instanceof Error ? err.message : 'An error occurred while fetching product');
        } finally {
            setIsLoading(false);
        }
    }, [id]);

    useEffect(() => {
        fetchProduct();
    }, [fetchProduct]);

    return {
        product,
        isLoading,
        error,
        refetch: fetchProduct
    };
};

/**
 * Hook for fetching a single product by slug
 */
export const useProductBySlug = (slug: string | null): UseProductReturn => {
    const [product, setProduct] = useState<Product | null>(null);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const fetchProduct = useCallback(async () => {
        if (!slug) return;

        setIsLoading(true);
        setError(null);

        try {
            const response = await getProductBySlug(slug);

            if (response.success) {
                setProduct(response.data);
            } else {
                setError(response.message || 'Failed to fetch product');
            }
        } catch (err) {
            setError(err instanceof Error ? err.message : 'An error occurred while fetching product');
        } finally {
            setIsLoading(false);
        }
    }, [slug]);

    useEffect(() => {
        fetchProduct();
    }, [fetchProduct]);

    return {
        product,
        isLoading,
        error,
        refetch: fetchProduct
    };
};

/**
 * Hook for fetching featured products
 */
export const useFeaturedProducts = (limit: number = 10): UseProductsReturn => {
    const [products, setProducts] = useState<Product[]>([]);
    const [totalItems, setTotalItems] = useState(0);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const fetchFeaturedProducts = useCallback(async () => {
        setIsLoading(true);
        setError(null);

        try {
            const response = await getFeaturedProducts(limit);

            if (response.success) {
                setProducts(response.data.items);
                setTotalItems(response.data.totalItems);
            } else {
                setError(response.message || 'Failed to fetch featured products');
            }
        } catch (err) {
            setError(err instanceof Error ? err.message : 'An error occurred while fetching featured products');
        } finally {
            setIsLoading(false);
        }
    }, [limit]);

    useEffect(() => {
        fetchFeaturedProducts();
    }, [fetchFeaturedProducts]);

    return {
        products,
        totalItems,
        isLoading,
        error,
        refetch: fetchFeaturedProducts
    };
};

/**
 * Hook for fetching products by category
 */
export const useProductsByCategory = (
    categoryCode: string | null,
    options: Omit<UseProductsOptions, 'filters'> = {}
): UseProductsReturn => {
    const [products, setProducts] = useState<Product[]>([]);
    const [totalItems, setTotalItems] = useState(0);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const fetchProductsByCategory = useCallback(async () => {
        if (!categoryCode) return;

        setIsLoading(true);
        setError(null);

        try {
            const response = await getProductsByCategory(categoryCode, options);

            if (response.success) {
                setProducts(response.data.items);
                setTotalItems(response.data.totalItems);
            } else {
                setError(response.message || 'Failed to fetch products by category');
            }
        } catch (err) {
            setError(err instanceof Error ? err.message : 'An error occurred while fetching products by category');
        } finally {
            setIsLoading(false);
        }
    }, [categoryCode, options]);

    useEffect(() => {
        fetchProductsByCategory();
    }, [fetchProductsByCategory]);

    return {
        products,
        totalItems,
        isLoading,
        error,
        refetch: fetchProductsByCategory
    };
};

/**
 * Hook for searching products
 */
export const useProductSearch = (
    searchTerm: string,
    options: Omit<UseProductsOptions, 'filters'> = {}
): UseProductsReturn => {
    const [products, setProducts] = useState<Product[]>([]);
    const [totalItems, setTotalItems] = useState(0);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const searchProductsData = useCallback(async () => {
        if (!searchTerm.trim()) {
            setProducts([]);
            setTotalItems(0);
            return;
        }

        setIsLoading(true);
        setError(null);

        try {
            const response = await searchProducts(searchTerm, options);

            if (response.success) {
                setProducts(response.data.items);
                setTotalItems(response.data.totalItems);
            } else {
                setError(response.message || 'Failed to search products');
            }
        } catch (err) {
            setError(err instanceof Error ? err.message : 'An error occurred while searching products');
        } finally {
            setIsLoading(false);
        }
    }, [searchTerm, options]);

    useEffect(() => {
        searchProductsData();
    }, [searchProductsData]);

    return {
        products,
        totalItems,
        isLoading,
        error,
        refetch: searchProductsData
    };
};

/**
 * Hook for fetching products by price range
 */
export const useProductsByPriceRange = (
    minPrice: number | null,
    maxPrice: number | null,
    options: Omit<UseProductsOptions, 'filters'> = {}
): UseProductsReturn => {
    const [products, setProducts] = useState<Product[]>([]);
    const [totalItems, setTotalItems] = useState(0);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const fetchProductsByPriceRange = useCallback(async () => {
        if (minPrice === null || maxPrice === null) return;

        setIsLoading(true);
        setError(null);

        try {
            const response = await getProductsByPriceRange(minPrice, maxPrice, options);

            if (response.success) {
                setProducts(response.data.items);
                setTotalItems(response.data.totalItems);
            } else {
                setError(response.message || 'Failed to fetch products by price range');
            }
        } catch (err) {
            setError(err instanceof Error ? err.message : 'An error occurred while fetching products by price range');
        } finally {
            setIsLoading(false);
        }
    }, [minPrice, maxPrice, options]);

    useEffect(() => {
        fetchProductsByPriceRange();
    }, [fetchProductsByPriceRange]);

    return {
        products,
        totalItems,
        isLoading,
        error,
        refetch: fetchProductsByPriceRange
    };
};

/**
 * Hook for fetching in-stock products
 */
export const useInStockProducts = (options: Omit<UseProductsOptions, 'filters'> = {}): UseProductsReturn => {
    const [products, setProducts] = useState<Product[]>([]);
    const [totalItems, setTotalItems] = useState(0);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const fetchInStockProducts = useCallback(async () => {
        setIsLoading(true);
        setError(null);

        try {
            const response = await getInStockProducts(options);

            if (response.success) {
                setProducts(response.data.items);
                setTotalItems(response.data.totalItems);
            } else {
                setError(response.message || 'Failed to fetch in-stock products');
            }
        } catch (err) {
            setError(err instanceof Error ? err.message : 'An error occurred while fetching in-stock products');
        } finally {
            setIsLoading(false);
        }
    }, [options]);

    useEffect(() => {
        fetchInStockProducts();
    }, [fetchInStockProducts]);

    return {
        products,
        totalItems,
        isLoading,
        error,
        refetch: fetchInStockProducts
    };
};

/**
 * Hook for fetching new arrivals products
 */
export const useNewArrivals = (limit: number = 10): UseProductsReturn => {
    const [products, setProducts] = useState<Product[]>([]);
    const [totalItems, setTotalItems] = useState(0);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const fetchNewArrivals = useCallback(async () => {
        setIsLoading(true);
        setError(null);

        try {
            const response = await getNewArrivals(limit);

            if (response.success) {
                setProducts(response.data.items);
                setTotalItems(response.data.totalItems);
            } else {
                setError(response.message || 'Failed to fetch new arrivals');
            }
        } catch (err) {
            setError(err instanceof Error ? err.message : 'An error occurred while fetching new arrivals');
        } finally {
            setIsLoading(false);
        }
    }, [limit]);

    useEffect(() => {
        fetchNewArrivals();
    }, [fetchNewArrivals]);

    return {
        products,
        totalItems,
        isLoading,
        error,
        refetch: fetchNewArrivals
    };
}; 